import { lazy, useState, useCallback } from 'react';
import {
  Box,
  Text,
  Group,
  Checkbox,
  TextInput,
  ActionIcon,
  ScrollArea,
  useMantineTheme,
  useComputedColorScheme,
  Badge,
} from '@mantine/core';
import { IconChecklist, IconPlus, IconTrash } from '@tabler/icons-react';
import { WidgetProps } from '../types';
import { registerWidget } from '../registry';
import { useWidgetData } from '../context/DashboardProvider';
import { FadeIn } from '../../../animations';
import { usePreferencesStore } from '../../../../stores';
import { LINE_HEIGHT_MAP, LETTER_SPACING_MAP } from '../../../../hooks/useAccessibilityStyles';

// ============================================================================
// Types
// ============================================================================

interface TodoItem {
  id: string;
  text: string;
  completed: boolean;
  priority: 'low' | 'medium' | 'high';
  createdAt: string;
}

// ============================================================================
// Mock Data
// ============================================================================

const initialTodos: TodoItem[] = [
  { id: '1', text: 'Review project proposals', completed: false, priority: 'high', createdAt: '2024-01-15' },
  { id: '2', text: 'Prepare quarterly report', completed: false, priority: 'high', createdAt: '2024-01-14' },
  { id: '3', text: 'Update team documentation', completed: true, priority: 'medium', createdAt: '2024-01-13' },
  { id: '4', text: 'Schedule client meeting', completed: false, priority: 'medium', createdAt: '2024-01-12' },
  { id: '5', text: 'Review pull requests', completed: true, priority: 'low', createdAt: '2024-01-11' },
];

// ============================================================================
// ToDoList Widget Component
// ============================================================================

function ToDoListWidget({ isCollapsed, filters, onFilterChange }: WidgetProps) {
  const theme = useMantineTheme();
  const colorScheme = useComputedColorScheme('light');
  const [todos, setTodos] = useState<TodoItem[]>(initialTodos);
  const [newTodoText, setNewTodoText] = useState('');
  const lineSpacing = usePreferencesStore((state) => state.lineSpacing);
  const letterSpacingPref = usePreferencesStore((state) => state.letterSpacing);

  const accessibilityStyles = {
    lineHeight: LINE_HEIGHT_MAP[lineSpacing],
    letterSpacing: LETTER_SPACING_MAP[letterSpacingPref],
  };

  // Filter state from widget filters
  const showCompleted = filters?.showCompleted !== false;

  if (isCollapsed) return null;

  const filteredTodos = showCompleted ? todos : todos.filter((t) => !t.completed);

  const handleToggle = (id: string) => {
    setTodos((prev) =>
      prev.map((todo) =>
        todo.id === id ? { ...todo, completed: !todo.completed } : todo
      )
    );
  };

  const handleAdd = () => {
    if (!newTodoText.trim()) return;

    const newTodo: TodoItem = {
      id: Date.now().toString(),
      text: newTodoText.trim(),
      completed: false,
      priority: 'medium',
      createdAt: new Date().toISOString(),
    };

    setTodos((prev) => [newTodo, ...prev]);
    setNewTodoText('');
  };

  const handleDelete = (id: string) => {
    setTodos((prev) => prev.filter((todo) => todo.id !== id));
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleAdd();
    }
  };

  const getPriorityColor = (priority: TodoItem['priority']) => {
    const colors = { low: 'gray', medium: 'blue', high: 'red' };
    return colors[priority];
  };

  const completedCount = todos.filter((t) => t.completed).length;
  const totalCount = todos.length;

  return (
    <FadeIn style={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
      <Box style={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
        {/* Header with count */}
        <Group justify="space-between" mb="sm">
          <Text size="xs" c="dimmed" style={accessibilityStyles}>
            {completedCount} of {totalCount} completed
          </Text>
          <Checkbox
            label="Show completed"
            size="xs"
            checked={showCompleted}
            onChange={(e) => onFilterChange?.({ ...filters, showCompleted: e.currentTarget.checked })}
          />
        </Group>

        {/* Add new todo */}
        <Group gap="xs" mb="sm">
          <TextInput
            placeholder="Add a new task..."
            size="xs"
            value={newTodoText}
            onChange={(e) => setNewTodoText(e.currentTarget.value)}
            onKeyDown={handleKeyDown}
            style={{ flex: 1 }}
          />
          <ActionIcon
            variant="filled"
            size="sm"
            onClick={handleAdd}
            disabled={!newTodoText.trim()}
          >
            <IconPlus size={14} />
          </ActionIcon>
        </Group>

        {/* Todo list */}
        <ScrollArea style={{ flex: 1, minHeight: 0 }}>
          {filteredTodos.length === 0 ? (
            <Box
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                height: '100%',
                minHeight: 60,
                color: theme.colors.gray[5],
              }}
            >
              <Text size="sm" style={accessibilityStyles}>No tasks</Text>
            </Box>
          ) : (
            filteredTodos.map((todo) => (
              <Group
                key={todo.id}
                p="xs"
                mb="xs"
                justify="space-between"
                wrap="nowrap"
                style={{
                  backgroundColor: colorScheme === 'dark' ? theme.colors.dark[6] : theme.colors.gray[0],
                  borderRadius: theme.radius.sm,
                  opacity: todo.completed ? 0.6 : 1,
                }}
              >
                <Group gap="xs" wrap="nowrap" style={{ flex: 1, minWidth: 0 }}>
                  <Checkbox
                    checked={todo.completed}
                    onChange={() => handleToggle(todo.id)}
                    size="xs"
                  />
                  <Box style={{ flex: 1, minWidth: 0 }}>
                    <Text
                      size="sm"
                      lineClamp={1}
                      td={todo.completed ? 'line-through' : undefined}
                      style={accessibilityStyles}
                    >
                      {todo.text}
                    </Text>
                  </Box>
                </Group>
                <Group gap={4} wrap="nowrap">
                  <Badge size="xs" variant="dot" color={getPriorityColor(todo.priority)}>
                    {todo.priority}
                  </Badge>
                  <ActionIcon
                    size="xs"
                    variant="subtle"
                    color="red"
                    onClick={() => handleDelete(todo.id)}
                  >
                    <IconTrash size={12} />
                  </ActionIcon>
                </Group>
              </Group>
            ))
          )}
        </ScrollArea>
      </Box>
    </FadeIn>
  );
}

// ============================================================================
// Widget Registration
// ============================================================================

registerWidget({
  id: 'todo-list',
  name: 'To-Do List',
  description: 'Personal task list with add, complete, and delete functionality',
  icon: IconChecklist,
  category: 'tasks',
  component: lazy(() => Promise.resolve({ default: ToDoListWidget })),
  sizes: {
    base: { cols: 12, rows: 5 },
    sm: { cols: 12, rows: 5 },
    md: { cols: 6, rows: 5 },
    lg: { cols: 4, rows: 5 },
    xl: { cols: 4, rows: 5 },
    tv: { cols: 4, rows: 4 },
  },
  sizeVariants: {
    variants: ['compact', 'standard', 'tall'],
    defaultVariant: 'standard',
    variantSizes: {
      compact: {
        base: { cols: 12, rows: 4 },
        sm: { cols: 6, rows: 4 },
        md: { cols: 4, rows: 4 },
        lg: { cols: 3, rows: 4 },
        xl: { cols: 3, rows: 4 },
        tv: { cols: 3, rows: 4 },
      },
      standard: {
        base: { cols: 12, rows: 5 },
        sm: { cols: 12, rows: 5 },
        md: { cols: 6, rows: 5 },
        lg: { cols: 4, rows: 5 },
        xl: { cols: 4, rows: 5 },
        tv: { cols: 4, rows: 5 },
      },
      tall: {
        base: { cols: 12, rows: 7 },
        sm: { cols: 12, rows: 7 },
        md: { cols: 6, rows: 7 },
        lg: { cols: 4, rows: 7 },
        xl: { cols: 4, rows: 7 },
        tv: { cols: 4, rows: 7 },
      },
    },
  },
  data: {
    sourceType: 'self',
    defaultFilters: { showCompleted: true },
  },
  filterLocation: 'inline',
  hasSettings: true,
  hasFilters: true,
  expandable: true,
});

export default ToDoListWidget;
